import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Input, version2 } from 'ui-components/components/input';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { GuidService } from 'ui-components/services/guid-service';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import ModalPortal from '../../../../../components/modal-portal';
import ProductTypeAheadInput from '../../../../../components/product-type-ahead-input/product-type-ahead-input';
import type { ProductSearchResult } from '../../../../../components/product-type-ahead-input/use-product-type-ahead-search';
import { messages } from './add-product-modal.messages';
import { createSupplierProduct, updateSupplierProduct } from '../../api/supplier-products-api';
import type { SupplierProduct } from '../../types/supplier-product-types';
import styles from './add-product-modal.module.less';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo';

type AddProductModalProps = {
  productId?: number;
  supplierId: number;
  editProductSupplierId?: string;
  initialSupplierSku?: string;
  initialCost?: number;
  close: () => void;
  onSuccess: () => void;
};

export const AddProductModal = ({
  productId,
  supplierId,
  editProductSupplierId,
  initialSupplierSku = '',
  initialCost,
  close,
  onSuccess,
}: AddProductModalProps) => {
  const intl = useIntl();
  const modalHeaderId = useMemo(() => GuidService.newGuid(), []);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedProductId, setSelectedProductId] = useState<number | undefined>(productId);
  const [productSearchValue, setProductSearchValue] = useState('');
  const [supplierSku, setSupplierSku] = useState(initialSupplierSku);
  const [cost, setCost] = useState<string>(initialCost !== undefined ? String(initialCost) : '');

  const [productError, setProductError] = useState<string>('');
  const [costError, setCostError] = useState<string>('');

  const isEditMode = editProductSupplierId !== undefined;

  const shouldShowSearchInfo = productSearchValue.length > 0 && productSearchValue.length < 3 && !selectedProductId;

  const onProductSearchChange = useCallback((value: string) => {
    setProductSearchValue(value);
  }, []);

  const onProductSelected = useCallback((product: ProductSearchResult) => {
    setSelectedProductId(Number(product.productId));
    setProductSearchValue(`${product.name} (${product.sku})`);
    setProductError('');
  }, []);

  const onSupplierSkuChanged = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierSku(event.target.value);
  }, []);

  const onCostChanged = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCost(event.target.value);
    setCostError('');
  }, []);

  const executeWithLoading = useCallback(
    async (action: () => Promise<SupplierProduct>, errorContent: React.ReactElement) => {
      setIsLoading(true);

      try {
        await action();
        onSuccess();
        close();
      } catch {
        NotificationService.showNotification({
          title: <FormattedMessage {...messages.errorNotificationTitle} />,
          content: errorContent,
          notificationType: NOTIFICATION_TYPE_ERROR,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess, close],
  );

  const handleSave = useCallback(async () => {
    let hasErrors = false;

    if (!isEditMode && !selectedProductId) {
      setProductError(intl.formatMessage(messages.productRequiredError));
      hasErrors = true;
    }

    const costNumber = Number(cost);
    if (cost === '' || cost === null || cost === undefined) {
      setCostError(intl.formatMessage(messages.costRequiredError));
      hasErrors = true;
    } else if (isNaN(costNumber) || costNumber < 0) {
      setCostError(intl.formatMessage(messages.costInvalidError));
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    if (isEditMode) {
      if (!editProductSupplierId) {
        return;
      }

      await executeWithLoading(
        () => updateSupplierProduct(editProductSupplierId, { supplierSku, cost: costNumber }),
        <FormattedMessage {...messages.updateErrorNotificationContent} />,
      );
    } else {
      if (!selectedProductId) {
        return;
      }

      await executeWithLoading(
        () => createSupplierProduct({ productId: selectedProductId, supplierId, supplierSku, cost: costNumber }),
        <FormattedMessage {...messages.errorNotificationContent} />,
      );
    }
  }, [isEditMode, editProductSupplierId, selectedProductId, supplierId, supplierSku, cost, executeWithLoading, intl]);

  const modalTitle = isEditMode ? messages.editSupplierTitle : messages.addProductTitle;

  return (
    <ModalPortal onClose={close}>
      <Modal modalHeaderId={modalHeaderId} size='medium'>
        <ModalHeader closeModal={close} id={modalHeaderId}>
          <Text textType={TextType.H3} value={modalTitle} />
        </ModalHeader>
        <ModalBody>
          {isLoading ? (
            <FlexColumn className={styles.loadingContainer}>
              <LoadingIndicator className={styles.loading} />
            </FlexColumn>
          ) : (
            <FlexColumn className={styles.content} gap={Gap.Space16}>
              {!isEditMode && !productId ? (
                <FlexColumn gap={Gap.Space8}>
                  <FlexRow gap={Gap.Space4}>
                    <Text textType={TextType.Label} value={messages.productLabel} />
                    <span className={styles.required}>*</span>
                  </FlexRow>
                  <FlexColumn gap={Gap.Space4}>
                    <ProductTypeAheadInput
                      disabled={isLoading}
                      errorMessage={productError || undefined}
                      onChange={onProductSearchChange}
                      onResultSelected={onProductSelected}
                      placeholder={intl.formatMessage(messages.productPlaceholder)}
                      value={productSearchValue}
                    />
                    {shouldShowSearchInfo ? (
                      <FlexRow className={styles.infoContainer} gap={Gap.Space4}>
                        <FontAwesomeIcon className={styles.infoIcon} icon={faCircleInfo} />
                        <Text className={styles.infoMessage} textType={TextType.Caption} value={messages.searchMinimumCharactersInfo} />
                      </FlexRow>
                    ) : null}
                  </FlexColumn>
                </FlexColumn>
              ) : null}
              <FlexColumn gap={Gap.Space8}>
                <Text textType={TextType.Label} value={messages.supplierSkuLabel} />
                <Input
                  disabled={isLoading}
                  onChange={onSupplierSkuChanged}
                  placeholder={intl.formatMessage(messages.supplierSkuPlaceholder)}
                  value={supplierSku}
                  version={version2}
                />
              </FlexColumn>
              <FlexColumn gap={Gap.Space8}>
                <FlexRow gap={Gap.Space4}>
                  <Text textType={TextType.Label} value={messages.unitCostLabel} />
                  <span className={styles.required}>*</span>
                </FlexRow>
                <Input
                  disabled={isLoading}
                  inlineMessage={costError || undefined}
                  inlineMessageType={costError ? 'error' : undefined}
                  inputMode='decimal'
                  onChange={onCostChanged}
                  placeholder={intl.formatMessage(messages.costPlaceholder)}
                  required
                  type='text'
                  value={cost}
                  version={version2}
                />
              </FlexColumn>
            </FlexColumn>
          )}
        </ModalBody>
        <ModalFooter>
          <Button disabled={isLoading} onClick={close} theme={THEME_CONSTANTS.tertiary}>
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button disabled={isLoading} onClick={handleSave} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.save} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
